import cn from "classnames";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars";
import AutoSizer from "react-virtualized-auto-sizer";
import { FCX, ID, Tooltip, useUuid } from "@datassential/platform-ui-lib";
import { getAxisLabels } from "./GroupsColumnsLineChart.helpers";
import Curves from "./Curves";
import "./GroupsColumnsLineChart.styles.scss";

export type ChartValue = number | null;

export interface ChartOption {
    id: ID;
    title: string;
}

export interface Series {
    id: ID;
    title: ReactNode;
    color: string;
    values: Array<{
        groupId: ID;
        columnId: ID;
        value: ChartValue;
    }>;
}

export const GROUP_GAP = 0;

const isNumber = (value: unknown): value is number => typeof value === "number";

const GroupsColumnsLineChart: FCX<{
    series: Series[];
    groups: ChartOption[];
    columns: ChartOption[];
    renderValue: (value: ChartValue) => ReactNode;
    renderTooltipTitle?: (groupTitle: string, columnTitle: string) => ReactNode;
    activeSeriesId?: ID;
    animationKey?: ID;
}> = ({
    series,
    groups,
    columns,
    activeSeriesId = null,
    renderValue,
    renderTooltipTitle = (groupTitle, columnTitle) => `${groupTitle} ${columnTitle}`,
    animationKey,
    className,
    testId,
    style,
}) => {
    const id = useUuid();
    const scrollRef = useRef<Scrollbars>(null);
    const [activeGroupId, setActiveGroupId] = useState<ID>(null);
    const [activeColumnId, setActiveColumnId] = useState<ID>(null);

    useEffect(() => {
        scrollRef.current?.scrollToRight();
    }, [groups, columns]);

    const allValues = useMemo(
        (): number[] => series
            .flatMap(s => s.values.map(v => v.value))
            .filter(isNumber),
        [series],
    );

    const maxValue = useMemo(() => (allValues.length ? Math.max(...allValues) : 0), [allValues]);
    const minValue = useMemo(() => (allValues.length ? Math.min(...allValues) : 0), [allValues]);

    const labels = useMemo(() => getAxisLabels(minValue, maxValue), [maxValue, minValue]);

    const getTooltipValue = useCallback(
        (seriesId: ID, groupId: ID, columnId: ID): ChartValue => {
            const seriesItem = series.find(s => s.id === seriesId);
            const valueItem = seriesItem?.values.find(
                v => v.groupId === groupId && v.columnId === columnId,
            );
            return valueItem?.value ?? null;
        },
        [series],
    );

    const activeGroup = useMemo(() => groups.find(g => g.id === activeGroupId), [activeGroupId, groups]);
    const activeColumn = useMemo(() => columns.find(c => c.id === activeColumnId), [activeColumnId, columns]);

    const tooltipData = useMemo(
        () => series
            .map(s => ({
                id: s.id,
                title: s.title,
                color: s.color,
                value: getTooltipValue(s.id, activeGroup?.id, activeColumn?.id),
            }))
            .sort((a, b) => {
                if (b.value === null) return -1;
                if (a.value === null) return 1;
                return b.value - a.value;
            }),
        [series, activeGroup, activeColumn, getTooltipValue],
    );

    return (
        <div className={cn("GroupsColumnsLineChart", className)} style={style} data-testid={testId}>
            <div className="GroupsColumnsLineChart__viewport">
                <div className="GroupsColumnsLineChart__y-axis">
                    {labels.map((label, index) => (
                        <div
                            key={label}
                            className="GroupsColumnsLineChart__y-axis-label"
                            data-testid={`textLineChartYAxisLabel_${index}`}
                        >
                            {renderValue(label)}
                        </div>
                    ))}
                </div>
                <Scrollbars
                    ref={scrollRef}
                    className="GroupsColumnsLineChart__view"
                    style={{ width: "100%", height: "100%" }}
                    hideTracksWhenNotNeeded
                    renderView={props => <div {...props} className="GroupsColumnsLineChart__scroll" />}
                    renderTrackHorizontal={({ style: trackStyle, ...props }) => (
                        <div
                            {...props}
                            className="GroupsColumnsLineChart__scrollbar-track"
                            style={{ ...trackStyle, left: 0, right: 0 }}
                        />
                    )}
                    renderThumbHorizontal={props => (
                        <div {...props} className="GroupsColumnsLineChart__scrollbar-thumb" />
                    )}
                    renderTrackVertical={() => <div style={{ display: "none" }} />}
                    renderThumbVertical={() => <div style={{ display: "none" }} />}
                >
                    <div className="GroupsColumnsLineChart__view-inner">
                        <div className="GroupsColumnsLineChart__groups">
                            <div className="GroupsColumnsLineChart__curves">
                                <AutoSizer>
                                    {({ width, height }) => (
                                        <Curves
                                            key={animationKey}
                                            series={series}
                                            groups={groups}
                                            columns={columns}
                                            labels={labels}
                                            height={height}
                                            width={width}
                                            groupGap={GROUP_GAP}
                                            activeSeriesId={activeSeriesId}
                                            activeColumnId={activeColumnId}
                                            activeGroupId={activeGroupId}
                                        />
                                    )}
                                </AutoSizer>
                            </div>
                            {groups.map((g, index) => (
                                <div
                                    key={g.id}
                                    className="GroupsColumnsLineChart__group"
                                    data-testid={`blockLineChartGroup_${index}`}
                                >
                                    <div className="GroupsColumnsLineChart__columns">
                                        {columns.map((c, cIndex) => (
                                            <div key={c.id} className="GroupsColumnsLineChart__bar">
                                                <div
                                                    className="GroupsColumnsLineChart__bar-inner"
                                                    data-tooltip-id={id}
                                                    onMouseEnter={() => {
                                                        setActiveGroupId(g.id);
                                                        setActiveColumnId(c.id);
                                                    }}
                                                    onMouseLeave={() => {
                                                        setActiveGroupId(null);
                                                        setActiveColumnId(null);
                                                    }}
                                                />
                                                <div
                                                    className="GroupsColumnsLineChart__bar-label"
                                                    data-testid={`textLineChartBarLabel_${index}_${cIndex}`}
                                                >
                                                    {c.title?.substring(0, 3)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div
                                        className="GroupsColumnsLineChart__group-label"
                                        data-testid={`textLineChartGroupLabel_${index}`}
                                    >
                                        {g.title}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Scrollbars>
                <Tooltip
                    id={id}
                    place="right"
                    float
                    style={{ animation: "none", transition: "none", zIndex: 10 }}
                >
                    <div className="GroupsColumnsLineChart__tooltip">
                        <div
                            className="GroupsColumnsLineChart__tooltip-title"
                            data-testid="textLineChartTooltipTitle"
                        >
                            {renderTooltipTitle(activeGroup?.title ?? "", activeColumn?.title ?? "")}
                        </div>
                        {tooltipData.map((s, index) => (
                            <div
                                key={s.id}
                                className={cn(
                                    "GroupsColumnsLineChart__tooltip-row",
                                    activeSeriesId !== null && activeSeriesId !== s.id && "is-dimmed",
                                    activeSeriesId === s.id && "is-active",
                                )}
                                data-testid={`blockLineChartTooltipRow_${index}`}
                            >
                                <div data-testid={`textLineChartTooltipRowTitle_${index}`}>{s.title}</div>
                                <div
                                    className="GroupsColumnsLineChart__tooltip-row-color"
                                    style={{ background: s.color }}
                                    data-testid={`blockLineChartTooltipRowColor_${index}`}
                                />
                                <div className="GroupsColumnsLineChart__tooltip-row-value">
                                    {renderValue(s.value)}
                                </div>
                            </div>
                        ))}
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};

export default GroupsColumnsLineChart;
